import { useEffect, useState } from "react";
import { useTheme } from "@/context/ThemeContext";
import { Paintbrush, X } from "lucide-react";

export default function ThemeCustomizer({ adminOnly = false, isAdmin = false }) {
    const { primaryHsl, setPrimaryHsl, updateTheme, presets } = useTheme();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const apply = (hsl) => {
        if (adminOnly && isAdmin) {
            updateTheme(hsl);
        } else {
            setPrimaryHsl(hsl);
        }
    };

    return (
        <div className="relative">
            <button
                data-testid="theme-toggle-button"
                onClick={() => setOpen(!open)}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-colors text-xs font-mono uppercase tracking-widest"
                title="Customize theme"
            >
                <Paintbrush className="w-3.5 h-3.5" /> Theme
            </button>
            {open && (
                <div className="absolute bottom-full mb-3 right-0 z-40 w-72 text-slate-900 bg-white border border-slate-300 shadow-[6px_6px_0_0_rgba(15,23,42,0.9)]" data-testid="theme-customizer-panel">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                        <div>
                            <div className="label-kicker text-slate-500">Theme</div>
                            <div className="font-display font-bold uppercase text-sm">
                                Accent Color
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} className="p-1">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="p-4">
                        <div className="grid grid-cols-2 gap-2">
                            {presets.map((p) => (
                                <button
                                    key={p.hsl}
                                    data-testid={`theme-preset-${p.hsl.replace(/[^a-z0-9]/gi, "-")}`}
                                    onClick={() => apply(p.hsl)}
                                    className={`flex items-center gap-2 px-3 py-2 border text-left transition-colors ${
                                        primaryHsl === p.hsl
                                            ? "border-slate-900 bg-slate-50"
                                            : "border-slate-200 hover:border-slate-400"
                                    }`}
                                >
                                    <span
                                        className="w-5 h-5 border border-slate-300"
                                        style={{ background: `hsl(${p.hsl})` }}
                                    />
                                    <span className="text-xs font-display font-bold uppercase">
                                        {p.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {adminOnly && !isAdmin && (
                            <p className="text-[11px] font-mono text-slate-500 mt-3">
                                Preview only. Log in as admin to persist theme.
                            </p>
                        )}
                        {adminOnly && isAdmin && (
                            <p className="text-[11px] font-mono text-emerald-700 mt-3">
                                ✓ Changes saved to all visitors
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
